import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';

interface Props {
  children: React.ReactNode;
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught:', error, info);
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(this.state.error, this.reset);
    }

    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh" p={3}>
        <Paper elevation={2} sx={{ p: 4, maxWidth: 560, width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            An unexpected error occurred. You can try again, or reload the page.
          </Typography>
          {import.meta.env.DEV && (
            <Typography
              variant="caption"
              component="pre"
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              {this.state.error.stack || this.state.error.message}
            </Typography>
          )}
          <Box mt={3} display="flex" gap={1}>
            <Button variant="contained" onClick={this.reset}>
              Try again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
